import React from 'react';
import gql from 'graphql-tag';
import { graphql } from '@apollo/react-hoc';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import pickBy from 'lodash/pickBy';
import format from 'date-fns/format';
import { FormattedMessage } from 'react-intl';
import filesaver from 'file-saver';
import { compose, withProps } from 'react-recompose';
import { generateCsvForDownload } from '@purple/match-graphql-schema/lib/activeMatchExportUtils';

import { SimpleGraphQLComponent, withSetVariablesProp } from '../../utils/apollo';
import { HEAccountQuery } from '../../utils/subscriptions';
import { queryErrorsToWarnings } from '../../components/GraphQLError';
import Button from '../../components/Button';
import Progress from '../../components/Progress';
import Modal from '../../components/Modal';
import { authorizedImpersonatorWithRole } from '../Authorization';
import { parseISODate } from '../VisitsAvailability/utils';
import * as constants from '../Authorization/constants';

import {
  DOWNLOAD_LIMIT,
  SHOW_YEAR,
  SHOW_LAST_EXPORT,
  SHOW_RECENT,
  STUDENT_CONNECTION_FRAGMENT,
} from './constants';

import messages from './messages';
import './styles.css';

export const CSV_FILENAME = 'student-connections.csv';
export const UNEXPORTED = 'unexported';

const formatConnectionDateForCsv = (date) => {
  if (!date) return date;

  const parsedDate = parseISODate(date);
  return Number.isNaN(parsedDate.getTime()) ? date : format(parsedDate, 'yyyy-MM-dd');
};

const formatConnectionsForCsv = (connections) => connections.map((connection) => ({
  ...connection,
  dateUpdated: formatConnectionDateForCsv(connection.dateUpdated),
  dateSubmitted: formatConnectionDateForCsv(connection.dateSubmitted),
}));

const queryNames = [
  'amStudentConnectionsBySchoolYearCount',
  'amStudentConnectionsSinceLastExportCount',
  'amStudentConnectionsRecentCount',
  'amStudentConnectionsBySchoolYear',
  'amStudentConnectionsSinceLastExport',
  'amStudentConnectionsRecent',
  'studentConnections',
];

export class DownloadConnectionsComponent extends React.PureComponent {
  static propTypes = {
    viewer: PropTypes.object,
    onDownload: PropTypes.func.isRequired,
    setVariables: PropTypes.func.isRequired,
    schoolYear: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    recentDays: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    downloadType: PropTypes.string,
    setDropdownBtnRef: PropTypes.func.isRequired,
    logActiveMatchConnectionsDownload: PropTypes.func,
    warnings: PropTypes.array,
    heAccount: PropTypes.object,
  };

  constructor(props) {
    super(props);
    this.state = {
      openModal: false,
      downloadReady: false,
      connections: [],
      requestProgress: 0,
    };
  }

  componentWillReceiveProps(nextProps) {
    const { setVariables, downloadType } = nextProps;

    let viewQueryName = '';
    /* istanbul ignore else */
    if (downloadType === SHOW_LAST_EXPORT) {
      viewQueryName = 'amStudentConnectionsSinceLastExport';
    } else if (downloadType === SHOW_YEAR) {
      viewQueryName = 'amStudentConnectionsBySchoolYear';
    } else if (downloadType === SHOW_RECENT) {
      viewQueryName = 'amStudentConnectionsRecent';
    }

    const nextConnections = get(nextProps, `viewer.${viewQueryName}`);
    const currentConnections = get(this.props, `viewer.${viewQueryName}`);

    if (nextConnections && (!currentConnections || nextConnections.pagination.page !== currentConnections.pagination.page)) {
      const { hasNextPage } = nextConnections.pagination;
      // eslint-disable-next-line react/no-access-state-in-setstate
      const connections = this.state.connections.concat(nextConnections.results);
      this.setState({
        requestProgress: (connections.length / nextConnections.pagination.totalCount) * 100,
        connections,
        downloadReady: !hasNextPage,
      });
      if (hasNextPage) {
        setVariables({ page: nextConnections.pagination.page + 1 });
      }
    }
  }

  downloadConnections = () => {
    const { setVariables, downloadType } = this.props;

    this.setState({ openModal: true, downloadReady: false, connections: [], requestProgress: 0 });
    setVariables({
      runYearConnectionsQuery: (downloadType === SHOW_YEAR),
      runUnexportedConnectionsQuery: (downloadType === SHOW_LAST_EXPORT),
      runRecentConnectionsQuery: (downloadType === SHOW_RECENT),
      page: 1,
    });
  };

  closeModal = () => {
    this.setState({ openModal: false });
  };

  cancelDownload = () => {
    this.finishDownload();
  };

  finishDownload = () => {
    this.props.setVariables({
      runYearConnectionsQuery: false,
      runUnexportedConnectionsQuery: false,
      runRecentConnectionsQuery: false,
    });
    this.setState({ connections: [] });
    this.closeModal();
  };

  downloadFile = () => {
    const { heAccount: { activeSubscriptions } = {} } = this.props;
    const camino = (activeSubscriptions || []).includes('CAMINO');
    const connections = formatConnectionsForCsv(this.state.connections);

    const csvBlob = new Blob(
      [generateCsvForDownload(connections, { connection: true, camino })],
      { type: 'text/csv;charset=utf-8;' },
    );

    filesaver.saveAs(csvBlob, CSV_FILENAME);
    this.props.onDownload();
    const { schoolYear, recentDays, downloadType } = this.props;
    const input = pickBy({
      schoolYear: downloadType === SHOW_YEAR ? schoolYear : undefined,
      recentDays: downloadType === SHOW_RECENT ? recentDays : undefined,
      sinceLastExport: downloadType === SHOW_LAST_EXPORT ? true : undefined,
    });

    this.props.logActiveMatchConnectionsDownload({ variables: { input } });
    this.finishDownload();
  };

  render() {
    const { viewer, downloadType, warnings } = this.props;
    const temporarilyUnavailable = (warnings && warnings.length > 0);
    const { openModal, downloadReady, requestProgress } = this.state;
    let viewQueryName = '';
    if (downloadType === SHOW_LAST_EXPORT) {
      viewQueryName = 'amStudentConnectionsSinceLastExportCount';
    } else if (downloadType === SHOW_YEAR) {
      viewQueryName = 'amStudentConnectionsBySchoolYearCount';
    } else if (downloadType === SHOW_RECENT) {
      viewQueryName = 'amStudentConnectionsRecentCount';
    }

    const connectionsCount = get(viewer, viewQueryName);
    const enabled = connectionsCount && connectionsCount.pagination.totalCount > 0;

    return (
      <Modal
        open={openModal}
        trigger={(
          <Button ref={(el) => this.props.setDropdownBtnRef(el)} primary onClick={this.downloadConnections} disabled={!enabled}>
            <FormattedMessage {...messages.download} />
          </Button>
)}
      >
        <Modal.Content>
          <Progress percent={requestProgress} color="teal">
            {!downloadReady && <FormattedMessage {...messages.downloadingConnections} />}
            {downloadReady && <FormattedMessage {...messages.downloadReady} />}
            {!downloadReady && temporarilyUnavailable && <p><b><FormattedMessage {...messages.temporarilyUnavailable} /></b></p>}
          </Progress>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={this.cancelDownload}>
            <FormattedMessage {...messages.cancel} />
          </Button>
          <Button
            primary
            disabled={!downloadReady}
            onClick={this.downloadFile}
          >
            <FormattedMessage {...messages.download} />
          </Button>
        </Modal.Actions>
      </Modal>
    );
  }
}

// placeholder for school year, used to avoid errors by setting it to null or 0
const SCHOOL_YEAR_PLACEHOLDER = 1900;

const query = gql`
  query AMPlusDownloadConnections(
    $schoolYear: Int,
    $page: Int,
    $limit: Int,
    $recentDays: Int,
    $runYearConnectionsCount: Boolean!,
    $runUnexportedConnectionsCount: Boolean!,
    $runRecentCount: Boolean!,
    $runYearConnectionsQuery: Boolean!,
    $runUnexportedConnectionsQuery: Boolean!,
    $runRecentConnectionsQuery: Boolean!,
  ) {
    viewer {
      id
      amStudentConnectionsBySchoolYearCount:
        amStudentConnectionsBySchoolYear(schoolYear: $schoolYear, page: $page, limit: $limit) @include(if: $runYearConnectionsCount) {
        pagination {
          totalCount
        }
      }
      amStudentConnectionsSinceLastExportCount:
        amStudentConnectionsSinceLastExport(page: 1, limit: $limit) @include(if: $runUnexportedConnectionsCount) {
        pagination {
          totalCount
        }
      }
      amStudentConnectionsRecentCount:
        amStudentConnectionsRecent(page: 1, limit: $limit, recentDays: $recentDays) @include(if: $runRecentCount) {
          pagination {
            totalCount
          }
      }
      amStudentConnectionsBySchoolYear(schoolYear: $schoolYear, page: $page, limit: $limit) @include(if: $runYearConnectionsQuery) {
        results {
          ...AMStudentResults
        }
        pagination {
          totalCount
          page
          hasNextPage
        }
      }
      amStudentConnectionsSinceLastExport(page: $page, limit: $limit) @include(if: $runUnexportedConnectionsQuery) {
        results {
          ...AMStudentResults
        }
        pagination {
          totalCount
          page
          hasNextPage
        }
      }
      amStudentConnectionsRecent(page: $page, limit: $limit, recentDays: $recentDays) @include(if: $runRecentConnectionsQuery) {
        results {
            ...AMStudentResults
          }
          pagination {
            totalCount
            page
            hasNextPage
          }
      }
    }
  }
  ${STUDENT_CONNECTION_FRAGMENT}
`;

const queryData= {
  initialVariables: {
    page: 1,
    limit: DOWNLOAD_LIMIT,
    downloadType: null,
    schoolYear: SCHOOL_YEAR_PLACEHOLDER,
    runYearConnectionsQuery: false,
    runUnexportedConnectionsQuery: false,
    runUnexportedConnectionsCount: false,
    runYearConnectionsCount: false,
    runRecentCount: false,
    runRecentConnectionsQuery: false,
    recentDays: null,
  },
  prepareVariables: (variables) => {
    const { downloadType } = variables;
    variables.runUnexportedConnectionsCount = (downloadType === SHOW_LAST_EXPORT);
    variables.runYearConnectionsCount = (downloadType === SHOW_YEAR);
    variables.runRecentCount = (downloadType === SHOW_RECENT);

    /* istanbul ignore next */
    if (variables.schoolYear === UNEXPORTED) {
      variables.schoolYear = SCHOOL_YEAR_PLACEHOLDER;
      variables.runYearConnectionsCount = false; // ensure the placeholder is not send
    }
    return variables;
  },
};

const graphqlData = graphql(query, {
  options: (props) => {
    const variables = { ...queryData.initialVariables };
    variables.downloadType = get(props, 'downloadType');
    variables.schoolYear = parseInt(get(props, 'schoolYear'), 10) || SCHOOL_YEAR_PLACEHOLDER;
    variables.recentDays = get(props, 'recentDays') || 0;
    queryData.prepareVariables(variables);
    return {
      errorPolicy: 'all',
      fetchPolicy: 'network-only',
      variables,
    };
  },
  props: compose(
    queryErrorsToWarnings(queryNames),
    withSetVariablesProp,
  ),
});

export const DownloadConnectionsContainer = compose(
  SimpleGraphQLComponent(graphqlData),
  graphql(HEAccountQuery),
  withProps(({ data: { viewer } }) => ({
    heAccount: viewer && viewer.heAccount,
  })),
)(DownloadConnectionsComponent);

const DisabledButton = () => (
  <Button primary disabled>
    <FormattedMessage {...messages.download} />
  </Button>
);

export default authorizedImpersonatorWithRole(
  [constants.IMPERSONATOR_SUPER_ADMIN_ROLE],
  { failure: DisabledButton },
)(DownloadConnectionsContainer);
